import { changeLocale } from "@shared/actions";
import {
  toggleContactsModal,
  toggleInfoModal,
  toggleSportBetsHistoryModal,
} from "@shared/actions/lobby";
import {
  ContactsModal,
  ModalInfo,
  Spinner,
  SportBetsHistoryModal,
} from "@shared/components";
import { ErrorFetchingView } from "@shared/components/error-fetching-view/error-fetching-view";
import { useGameErrorStatus, useGamesFetch } from "@shared/hooks/games";
import {
  useLobbyParams,
  useModalContactsIsShow,
  useModalInfoIsShow,
  useModalSportBetsHistoryIsShow,
} from "@shared/hooks/lobby";
import { useUserError, useUserLobby, useUserState } from "@shared/hooks/user";
import { useApplyFilters, useDefaultLocale } from "@shared/hooks/uses-services";
import { useQueryAllData } from "@shared/modules/core/query/use-query-app";
import {
  localStorageService,
  userWatchActivityService,
} from "@shared/services";
import { hasUser } from "@shared/utils/users";
import { memo, useEffect, type ReactNode } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { PollerComponent } from "./poller-component";

// import { RulesModal } from "@shared/modules/rules/ui/rules-modal";

const DEFAULT_PANIC_URL = "https://www.google.com/search?igu=1";

interface IWithPoller {
  children: ReactNode;
}

export const AppLayout = memo((props: IWithPoller) => {
  const dispatch = useDispatch();
  const { fetchAllData, isLoading } = useQueryAllData();
  const { locale } = useDefaultLocale();
  const userLobbyParams = useUserLobby();
  const params = useLobbyParams() ?? {};
  const gamesFetchStatus = useGamesFetch();
  const userState = useUserState();
  const isAuth = hasUser(userState);
  const history = useHistory();
  const isGamePage = history?.location?.pathname.includes("/games/");
  const isContactsModalShown = useModalContactsIsShow();
  const isInfoModalShown = useModalInfoIsShow();
  const isSportBetsHistoryModalShown = useModalSportBetsHistoryIsShow();
  const { handleFilterApply } = useApplyFilters();
  const { hasLimit: isUserErrorLimit } = useUserError();

  const { hasLimit: isGameRunErrorLimit } = useGameErrorStatus();

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (gamesFetchStatus === "fulfilled" && isAuth) {
      handleFilterApply();
    }
  }, [gamesFetchStatus, handleFilterApply, isAuth]);

  useEffect(() => {
    if (locale) {
      dispatch(changeLocale(locale));
    }

    if (isAuth) {
      userWatchActivityService.init();
    }

    localStorageService.saveData(
      "lobby-info",
      JSON.stringify({
        casino_id: params?.casinoId,
        bank_group_id: params?.bankGroupId,
        lobby_id: params?.lobbyId,
        player_name: userState?.user?.displayable_name,
      })
    );

    return () => userWatchActivityService.dispose();
  }, [
    params?.bankGroupId,
    params?.casinoId,
    params?.lobbyId,
    userState?.user?.displayable_name,
  ]);

  if (userLobbyParams?.isPanic) {
    const { panicUrl } = userLobbyParams;
    document.title = "welcome";
    return (
      <>
        <iframe
          title="iframe"
          className="panic-iframe"
          src={panicUrl ?? DEFAULT_PANIC_URL}
        />
        <PollerComponent />
      </>
    );
  }

  if ((isUserErrorLimit && !isAuth) || (isGamePage && isGameRunErrorLimit)) {
    return <ErrorFetchingView />;
  }

  if (isLoading) {
    return (
      <>
        <PollerComponent />
        <Spinner />
      </>
    );
  }

  return (
    <>
      {isContactsModalShown && (
        <ContactsModal
          isOpen={isContactsModalShown}
          onClose={() => dispatch(toggleContactsModal(false))}
        />
      )}
      {isInfoModalShown && (
        <ModalInfo
          isOpen={isInfoModalShown}
          onClose={() => dispatch(toggleInfoModal(false))}
        />
      )}
      {isSportBetsHistoryModalShown && (
        <SportBetsHistoryModal
          isOpen={isSportBetsHistoryModalShown}
          onClose={() => dispatch(toggleSportBetsHistoryModal(false))}
        />
      )}
      <PollerComponent />
      {props.children}

      {/*-------------------------------FEATURES*------------------------------------------/}
        {/*{hasUser(userState) && isSweepstakesShown && <TermsOfUseModal />}*/}
    </>
  );
});

AppLayout.displayName = "AppLayout";
